import type { TimeOffCalendarOrgFilter } from './components/TimeOffCalendarOrgFilter';

export interface TimeOffCalendarEmployeeFilterPref {
  upn: string;
  displayName: string;
}

export interface TimeOffCalendarPrefs {
  currentView: string;
  currentDateIso: string;
  activeTargetIds: string[];
  showPast: boolean;
  showCancelled: boolean;
  employeeFilter: TimeOffCalendarEmployeeFilterPref | null;
  orgFilter: TimeOffCalendarOrgFilter;
}

const PREFS_KEY_PREFIX = 'webopsui:time-off-calendar-prefs:v2';

const CALENDAR_VIEWS = new Set(['day', 'week', 'workWeek', 'month']);

/** Map removed or unknown scheduler views back to a supported default. */
export function normalizeCalendarView(view: string): string {
  if (CALENDAR_VIEWS.has(view)) return view;
  if (view === 'timelineWeek') return 'week';
  if (view === 'timelineWorkWeek') return 'workWeek';
  return 'month';
}

export function calendarPrefsStorageKey(username: string | null): string {
  const userKey = (username ?? 'anonymous').trim().toLowerCase().replace(/[^a-z0-9@._-]+/g, '-');
  return `${PREFS_KEY_PREFIX}:${userKey}`;
}

export function defaultCalendarPrefs(now = new Date()): TimeOffCalendarPrefs {
  return {
    currentView: 'month',
    currentDateIso: now.toISOString(),
    activeTargetIds: [],
    showPast: false,
    showCancelled: false,
    employeeFilter: null,
    orgFilter: { kind: 'all' }
  };
}

function parseOrgFilter(raw: unknown): TimeOffCalendarOrgFilter {
  if (!raw || typeof raw !== 'object') return { kind: 'all' };
  const value = raw as { kind?: string; value?: string };
  if (value.kind === 'company' && typeof value.value === 'string' && value.value.trim()) {
    return { kind: 'company', value: value.value.trim() };
  }
  if (value.kind === 'department' && typeof value.value === 'string' && value.value.trim()) {
    return { kind: 'department', value: value.value.trim() };
  }
  return { kind: 'all' };
}

function parseEmployeeFilter(raw: unknown): TimeOffCalendarEmployeeFilterPref | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as { upn?: string; displayName?: string };
  const upn = typeof value.upn === 'string' ? value.upn.trim() : '';
  if (!upn) return null;
  return {
    upn,
    displayName: typeof value.displayName === 'string' ? value.displayName.trim() : ''
  };
}

export function loadCalendarPrefs(storageKey: string, now = new Date()): TimeOffCalendarPrefs {
  const defaults = defaultCalendarPrefs(now);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<TimeOffCalendarPrefs>;
    return {
      currentView: normalizeCalendarView(
        typeof parsed.currentView === 'string' ? parsed.currentView : defaults.currentView
      ),
      currentDateIso: typeof parsed.currentDateIso === 'string' ? parsed.currentDateIso : defaults.currentDateIso,
      activeTargetIds: Array.isArray(parsed.activeTargetIds)
        ? parsed.activeTargetIds.filter((id): id is string => typeof id === 'string')
        : [],
      showPast: Boolean(parsed.showPast),
      showCancelled: Boolean(parsed.showCancelled),
      employeeFilter: parseEmployeeFilter(parsed.employeeFilter),
      orgFilter: parseOrgFilter(parsed.orgFilter)
    };
  } catch {
    return defaults;
  }
}

export function saveCalendarPrefs(storageKey: string, prefs: TimeOffCalendarPrefs): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable
  }
}

export function parseCalendarPrefsDate(iso: string, fallback = new Date()): Date {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

export function resolveActiveTargetIds(
  savedIds: string[],
  availableTargetIds: string[]
): Set<string> {
  const available = new Set(availableTargetIds);
  if (savedIds.length === 0) {
    return new Set(availableTargetIds);
  }
  const filtered = savedIds.filter((id) => available.has(id));
  return new Set(filtered.length > 0 ? filtered : availableTargetIds);
}
